import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridValidRowModel } from '@mui/x-data-grid';

import { useAllureTable } from './useAllureTable.ts';
import { type ZoneRow } from '../Construction/allureTable.ts';
import { estCoach, nomAthlete, useMoi, type AthleteRattache } from '../Auth/moi.ts';

type Ligne = ZoneRow & { id: string };

// Colonne numerique editable : une borne NaN ("non configuree", ex: RECUP)
// s'affiche vide, et une cellule videe redevient NaN.
function colonneBorne(field: keyof ZoneRow & string, headerName: string, width = 110): GridColDef<Ligne> {
  return {
    field,
    headerName,
    type: 'number',
    width,
    editable: true,
    valueGetter: (value: number) => (typeof value === 'number' && isFinite(value) ? value : null),
    valueSetter: (value: number | null, row: Ligne) => ({
      ...row,
      [field]: value === null || value === undefined || isNaN(Number(value)) ? NaN : Number(value),
    }),
  };
}

const columns: GridColDef<Ligne>[] = [
  { field: 'code', headerName: 'Code', width: 80 },
  { field: 'nom', headerName: 'Allure', width: 160, editable: true },
  {
    field: 'pctVMA',
    headerName: '% VMA',
    type: 'number',
    width: 90,
    editable: true,
    // Stocke en fraction (0.91), saisi et affiche en pourcentage (91).
    valueGetter: (value: number) => (isFinite(value) ? Math.round(value * 1000) / 10 : null),
    valueSetter: (value: number | null, row: Ligne) => ({
      ...row,
      pctVMA: value === null || value === undefined ? NaN : Number(value) / 100,
    }),
  },
  colonneBorne('ratioMin', 'Ratio récup. min', 130),
  colonneBorne('ratioMax', 'Ratio récup. max', 130),
  colonneBorne('repsMin', 'Reps min'),
  colonneBorne('repsMax', 'Reps max'),
  colonneBorne('volMin', 'Volume min (m)', 130),
  colonneBorne('volMax', 'Volume max (m)', 130),
  colonneBorne('distMin', 'Dist. min (m)', 120),
  colonneBorne('distMax', 'Dist. max (m)', 120),
];

function versLignes(table: ZoneRow[]): Ligne[] {
  return table.map((z) => ({ ...z, id: z.code }));
}

function versTable(lignes: Ligne[]): ZoneRow[] {
  return lignes.map(({ id, ...zone }) => zone);
}

// Editeur des parametres d'allures de l'utilisateur, affiche sur la page
// Mes preferences (cf Preferences/Preferences.tsx). Modifiables par le coach
// seulement (les siens, ou ceux d'un athlete : page Athletes) ; l'athlete
// consulte ceux que son coach a regles.
export default function EditeurParametresAllures({ athlete = null }: { athlete?: AthleteRattache | null }) {
  const moi = useMoi();
  const lectureSeule = !estCoach(moi);
  const { table, personnalisee, chargement, enregistrer, reinitialiser } = useAllureTable(athlete?.ssoUserId ?? null);
  const [lignes, setLignes] = React.useState<Ligne[]>(() => versLignes(table));
  const [modifie, setModifie] = React.useState(false);
  const [enCours, setEnCours] = React.useState(false);
  const [message, setMessage] = React.useState<{ severity: 'success' | 'error'; texte: string } | null>(null);

  // Table (re)chargee depuis le serveur : on l'affiche, sauf si
  // l'utilisateur a deja commence a la modifier.
  React.useEffect(() => {
    if (!modifie) setLignes(versLignes(table));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  const processRowUpdate = React.useCallback((nouvelle: GridValidRowModel) => {
    const ligne = nouvelle as Ligne;
    setLignes((prec) => prec.map((l) => (l.id === ligne.id ? ligne : l)));
    setModifie(true);
    return ligne;
  }, []);

  const handleEnregistrer = () => {
    setEnCours(true);
    enregistrer(versTable(lignes))
      .then(() => {
        setModifie(false);
        setMessage({ severity: 'success', texte: 'Paramètres d\'allures enregistrés.' });
      })
      .catch((e: Error) => setMessage({ severity: 'error', texte: e.message }))
      .finally(() => setEnCours(false));
  };

  const handleAnnuler = () => {
    setLignes(versLignes(table));
    setModifie(false);
  };

  const handleReinitialiser = () => {
    setEnCours(true);
    reinitialiser()
      .then(() => {
        setModifie(false);
        setMessage({ severity: 'success', texte: 'Valeurs par défaut rétablies.' });
      })
      .catch((e: Error) => setMessage({ severity: 'error', texte: e.message }))
      .finally(() => setEnCours(false));
  };

  return (
    <Box id="parametres-allures" sx={{ mt: 6 }}>
      <h2>Zones d'allure</h2>

      <Typography sx={{ mb: 2 }}>
        Zones d'allure sont basés sur tes records et utilisées par ton coach pour la construction de séances et le générateur de modèles : % de VMA, ratio
        récupération / effort, nombre de répétitions, volume total et distance par répétition.
        {lectureSeule
          ? ' Ils sont réglés par ton coach à partir de tes performances ; une borne vide signifie « non configurée ».'
          : ' Double-clique sur une cellule pour la modifier ; une borne vide signifie « non configurée ».'}
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        {athlete
          ? personnalisee
            ? `${nomAthlete(athlete)} a ses propres paramètres d'allures.`
            : `${nomAthlete(athlete)} utilise les paramètres par défaut (valeurs du classeur Allures_repetitions).`
          : personnalisee
            ? 'Tu utilises tes propres paramètres d\'allures.'
            : 'Tu utilises les paramètres par défaut (valeurs du classeur Allures_repetitions).'}
      </Alert>

      {!lectureSeule && <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button variant="contained" onClick={handleEnregistrer} disabled={!modifie || enCours}>
          Enregistrer
        </Button>
        <Button variant="outlined" onClick={handleAnnuler} disabled={!modifie || enCours}>
          Annuler les modifications
        </Button>
        <Button color="warning" onClick={handleReinitialiser} disabled={!personnalisee || enCours}>
          Revenir aux valeurs par défaut
        </Button>
      </Box>}

      <DataGrid
        rows={lignes}
        columns={columns}
        loading={chargement}
        processRowUpdate={processRowUpdate}
        isCellEditable={() => !lectureSeule}
        onProcessRowUpdateError={(e) => setMessage({ severity: 'error', texte: String(e) })}
        hideFooter
        disableRowSelectionOnClick
        autoHeight
      />

      {message && (
        <Snackbar open autoHideDuration={6000} onClose={() => setMessage(null)}>
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.texte}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}
